import { useEffect, useState } from 'react';
import {
  produtosApi,
  type AtualizarProdutoRequest,
  type ProdutoResumo,
} from '@/features/produtos/api/produtosApi';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { InputMonetario } from '@/shared/ui/InputMonetario';
import { IconInfo } from '@/shared/ui/Icon';

type Modo =
  | { tipo: 'novo' }
  | { tipo: 'editar'; produto: ProdutoResumo };

type Props = {
  modo: Modo;
  aoFechar: () => void;
  aoSalvar: (dados: AtualizarProdutoRequest) => Promise<void>;
};

function arred2(v: number): number {
  return Math.round(v * 100) / 100;
}

function arred4(v: number): number {
  return Math.round(v * 10000) / 10000;
}

function inteiroNaoNegativo(valor: string): number {
  return Math.max(0, Math.trunc(Number(valor) || 0));
}

export function ProdutoFormModal({ modo, aoFechar, aoSalvar }: Props) {
  const editando = modo.tipo === 'editar';
  const [codigo, setCodigo] = useState(editando ? modo.produto.codigo : '');
  const [nome, setNome] = useState(editando ? modo.produto.nome : '');
  const [precoCompra, setPrecoCompra] = useState(editando ? modo.produto.precoCompra : 0);
  const [margem, setMargem] = useState(editando ? modo.produto.margemPercentual : 0);
  const [precoVenda, setPrecoVenda] = useState(editando ? modo.produto.precoUnitario : 0);
  const [quantidadeInicial, setQuantidadeInicial] = useState(0);
  const [ativo, setAtivo] = useState(editando ? modo.produto.ativo : true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  /** Se true, alterações em compra/margem recalculam o preço de venda. */
  const [vincular, setVincular] = useState(true);

  useEffect(() => {
    if (!vincular) return;
    setPrecoVenda(arred2(precoCompra * (1 + margem / 100)));
  }, [precoCompra, margem, vincular]);

  function aoMudarPrecoVenda(novo: number) {
    setPrecoVenda(novo);
    if (vincular && precoCompra > 0) {
      const novaMargem = arred4(((novo / precoCompra) - 1) * 100);
      setMargem(novaMargem < 0 ? 0 : novaMargem);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const dados = {
        nome,
        precoCompra,
        margemPercentual: margem,
        precoUnitario: precoVenda,
        ativo,
      };
      if (editando) {
        await aoSalvar(dados);
      } else {
        await produtosApi.criar({
          codigo: codigo.trim() || null,
          nome,
          precoCompra,
          margemPercentual: margem,
          precoUnitario: precoVenda,
          quantidadeInicial: editando ? undefined : quantidadeInicial,
        });
        await aoSalvar(dados);
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  const lucroBruto = arred2(precoVenda - precoCompra);

  return (
    <Modal
      aberto
      titulo={editando ? 'Editar produto' : 'Novo produto'}
      onFechar={aoFechar}
      rodape={
        <>
          <Button onClick={aoFechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variante="primary"
            onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
            disabled={salvando}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form className="form-grid" onSubmit={handleSubmit}>
        {erro && <div className="form-error">{erro}</div>}

        <Field
          rotulo="Código"
          dica={
            editando
              ? 'Código não pode ser alterado.'
              : 'Opcional. Se deixar vazio, será gerado no padrão PRD-NNNN.'
          }
        >
          {(id) => (
            <input
              id={id}
              className="input"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              disabled={editando}
              maxLength={50}
            />
          )}
        </Field>

        <Field rotulo="Nome" obrigatorio>
          {(id) => (
            <input
              id={id}
              className="input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
              minLength={2}
              maxLength={200}
            />
          )}
        </Field>

        <fieldset className="fieldset">
          <legend className="fieldset__legend">Precificação</legend>

          <label className="checkbox" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={vincular}
              onChange={(e) => setVincular(e.target.checked)}
            />
            <span>Calcular preço de venda automaticamente a partir da margem</span>
          </label>

          <div className="precificacao-grid">
            <Field rotulo="Preço de compra">
              {(id) => (
                <InputMonetario id={id} valor={precoCompra} onChange={setPrecoCompra} />
              )}
            </Field>

            <Field rotulo="Margem (%)" dica="Lucro sobre o preço de compra.">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={margem}
                  onChange={(e) => setMargem(Number(e.target.value) || 0)}
                />
              )}
            </Field>

            <Field rotulo="Preço de venda">
              {(id) => (
                <InputMonetario id={id} valor={precoVenda} onChange={aoMudarPrecoVenda} />
              )}
            </Field>
          </div>

          <p className="hint" style={{ marginTop: 8 }}>
            Lucro bruto por unidade:{' '}
            <strong>
              {lucroBruto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </strong>
          </p>
        </fieldset>

        {!editando && (
          <Field rotulo="Quantidade inicial">
            {(id) => (
              <div className="input-com-icone">
                <input
                  id={id}
                  className="input"
                  type="number"
                  step="1"
                  min="0"
                  value={quantidadeInicial}
                  onChange={(e) => setQuantidadeInicial(inteiroNaoNegativo(e.target.value))}
                />
                <span
                  className="info-icon"
                  tabIndex={0}
                  aria-label="Se informar quantidade, será criada uma entrada vinculada ao estoque deste produto."
                >
                  <IconInfo />
                  <span className="info-icon__tooltip" role="tooltip">
                    Se informar quantidade, será criada uma entrada vinculada ao estoque deste produto.
                  </span>
                </span>
              </div>
            )}
          </Field>
        )}

        {editando && (
          <Field rotulo="Status">
            {(id) => (
              <label htmlFor={id} className="checkbox">
                <input
                  id={id}
                  type="checkbox"
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                />
                <span>Ativo</span>
              </label>
            )}
          </Field>
        )}
      </form>
    </Modal>
  );
}
